import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  MapPin, Navigation, Star, Footprints, Bike, Clock,
  Phone, MessageCircle, HeartHandshake,
} from "lucide-react";
import BrandMark from "@/components/BrandMark";
import { useClientTheme } from "@/contexts/ThemeContext";
import { useSeo } from "@/lib/useSeo";
import {
  BRAND_NAME,
  BRAND_ADDRESS_STREET, BRAND_ADDRESS_CITY,
  BRAND_PHONE, BRAND_PHONE_DISPLAY,
  BRAND_WHATSAPP, BRAND_WHATSAPP_DISPLAY,
  BRAND_MAPS_URL, BRAND_WAZE_URL, BRAND_APPLE_MAPS_URL,
  BRAND_MAP_EMBED_URL, BRAND_REVIEWS_URL,
  BRAND_REVIEWS_RATING, BRAND_REVIEWS_COUNT,
  LOGIN_PHOTOS,
} from "@/lib/brand";

export default function Home() {
  const { i18n } = useTranslation();
  const isEN = i18n.language === "en";
  const { theme } = useClientTheme();
  const [photoIdx, setPhotoIdx] = useState(0);
  const waNum = BRAND_WHATSAPP.replace(/[^0-9]/g, "");

  // Iter 50/62 — SEO homepage: primary keyword phrase for Isola d'Elba
  // tourism. Covers "noleggio scooter Elba", "noleggio auto Elba",
  // "autonoleggio Elba", "noleggio Marciana Marina", "rent scooter Elba".
  useSeo({
    title: isEN
      ? "Rent Scooter & Car on Elba Island · Marciana Marina — Rent Lupi (since 1982)"
      : "Noleggio Scooter e Auto Isola d'Elba · Marciana Marina — Rent Lupi (dal 1982)",
    description: isEN
      ? "Rent scooter and rent car on Elba Island · Marciana Marina since 1982. In-house workshop, digital contract, instant online booking, Revolut deposit."
      : "Noleggio scooter e autonoleggio Isola d'Elba a Marciana Marina dal 1982 · Officina interna, contratto digitale, prenotazione online, caparra Revolut · Rent Lupi.",
    canonical: "https://rentlupielba.it/home",
  });

  useEffect(() => {
    LOGIN_PHOTOS.forEach((src) => { const i = new Image(); i.src = src; });
    const id = setInterval(() => setPhotoIdx((i) => (i + 1) % LOGIN_PHOTOS.length), 6500);
    return () => clearInterval(id);
  }, []);

  const hours = [
    { day: isEN ? "Mon – Fri" : "Lun – Ven", open: "9:00–13:00 · 15:00–19:00" },
    { day: isEN ? "Saturday" : "Sabato",     open: isEN ? "By appointment · 20:00–20:30" : "Su appuntamento · 20:00–20:30" },
    { day: isEN ? "Sunday" : "Domenica",     open: isEN ? "Returns only · 20:00–20:30" : "Solo riconsegne · 20:00–20:30" },
  ];

  return (
    <div className="space-y-12 pb-4" data-testid="customer-home">
      {/* ============ HERO with crossfade carousel (same look as login) ============ */}
      <section className="relative -mx-5 sm:-mx-5 overflow-hidden rounded-b-3xl">
        <div className="relative h-[60vh] min-h-[420px] max-h-[640px]">
          {LOGIN_PHOTOS.map((src, i) => {
            const active = i === photoIdx;
            return (
              <div
                key={src}
                className="absolute inset-0 pointer-events-none"
                style={{
                  backgroundImage: `url('${src}')`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  opacity: active ? 0.55 : 0,
                  transform: active ? "scale(1.05)" : "scale(1.0)",
                  transition: "opacity 2200ms cubic-bezier(0.4, 0, 0.2, 1), transform 12000ms ease-out",
                  willChange: "opacity, transform",
                }}
              />
            );
          })}
          {/* Theme-aware radial vignette: cinematic black in dark, cream in light. */}
          <div className="absolute inset-0 pointer-events-none"
               style={{
                 background: theme === "light"
                   ? "radial-gradient(ellipse at center, rgba(245,244,239,0.0) 0%, rgba(245,244,239,0.55) 60%, rgba(245,244,239,0.92) 100%)"
                   : "radial-gradient(ellipse at center, rgba(9,9,11,0.15) 0%, rgba(9,9,11,0.55) 60%, rgba(9,9,11,0.85) 100%)",
               }} />
          <div className="relative h-full flex flex-col items-center justify-end text-center px-5 pb-12">
            <BrandMark size="lg" showWordmark={false} />
            <h1
              className="mt-3 text-5xl sm:text-6xl text-[#FFD700] leading-none drop-shadow-[0_4px_24px_rgba(250,204,21,0.4)]"
              style={{ fontFamily: "'Caveat', cursive", fontWeight: 700, transform: "rotate(-3deg)" }}
              data-testid="home-brand-script"
            >
              Rent Lupi
            </h1>
            {/* Iter 62 — semantic SEO sub-heading with target keywords
                ("noleggio scooter Elba", "autonoleggio Marciana Marina"). Styled
                discretely so it fits under the Caveat H1 without stealing focus. */}
            <h2
              className="mt-2 text-sm sm:text-base text-zinc-300 tracking-wide uppercase"
              style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 500, letterSpacing: "0.08em" }}
              data-testid="home-seo-subhead"
            >
              {isEN
                ? "Scooter & Car Rental · Elba Island · Marciana Marina"
                : "Noleggio Scooter e Auto · Isola d'Elba · Marciana Marina"}
            </h2>
            <p
              className="mt-3 max-w-md text-zinc-100 leading-snug"
              style={{ fontFamily: "'Playfair Display', serif", fontStyle: "italic", fontWeight: 500, fontSize: "1.05rem" }}
              data-testid="home-brand-tagline"
            >
              {isEN ? (
                <>The most beautiful way to discover Elba: <span className="text-[#FFD700]">Rent, Explore, Smile!</span></>
              ) : (
                <>Il modo più bello di scoprire l&apos;Elba: <span className="text-[#FFD700]">Noleggia, Esplora, Sorridi!</span></>
              )}
            </p>
            <div className="mt-6 flex gap-3">
              <Link to="/catalog"
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#FFD700] hover:brightness-110 text-zinc-950 font-semibold transition shadow-lg"
                    data-testid="home-cta-catalog">
                <Bike className="w-5 h-5" />
                {isEN ? "Browse fleet" : "Vedi la flotta"}
              </Link>
              <a href={`https://wa.me/${waNum}`} target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-emerald-600/20 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-600/30 transition backdrop-blur"
                 data-testid="home-cta-whatsapp">
                <MessageCircle className="w-5 h-5" />
                WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ============ Help banner (small family business) ============ */}
      <section className="rounded-2xl border border-[#FFD700]/30 bg-[#FFD700]/[0.06] p-5 flex items-start sm:items-center gap-4" data-testid="home-help-banner">
        <div className="w-12 h-12 rounded-full bg-[#FFD700]/15 flex items-center justify-center flex-shrink-0">
          <HeartHandshake className="w-6 h-6 text-[#FFD700]" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-zinc-100 font-semibold">
            {isEN ? "Need a hand? We pick up the phone." : "Serve aiuto? Rispondiamo ancora al telefono."}
          </h3>
          <p className="text-sm text-zinc-400 mt-0.5">
            {isEN
              ? "We are a small family business. Calls or WhatsApp messages get answered by a real person — Marco or one of the team."
              : "Siamo una piccola azienda familiare. Telefonate e messaggi WhatsApp ti rispondono direttamente Marco o uno dei suoi."}
          </p>
        </div>
        <div className="hidden sm:flex flex-col gap-2">
          <a href={`tel:${BRAND_PHONE}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-zinc-900 border border-zinc-700 hover:border-[#FFD700] text-xs text-zinc-100 transition" data-testid="home-help-phone">
            <Phone className="w-3.5 h-3.5" /> {BRAND_PHONE_DISPLAY}
          </a>
          <a href={`https://wa.me/${waNum}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#25D366]/15 border border-[#25D366]/40 hover:bg-[#25D366]/25 text-[#25D366] text-xs font-medium transition" data-testid="home-help-wa">
            <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
          </a>
        </div>
      </section>

      {/* ============ Opening hours ============ */}
      <section data-testid="home-opening-hours">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-full bg-[#FFD700]/15 border border-[#FFD700]/40 flex items-center justify-center">
            <Clock className="w-4 h-4 text-[#FFD700]" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">{isEN ? "Schedule" : "Orari"}</p>
            <h2 className="text-2xl font-semibold text-zinc-100 leading-tight">
              {isEN ? "When we're open" : "Quando siamo aperti"}
            </h2>
          </div>
        </div>
        <div className="rounded-2xl border border-zinc-800 bg-zinc-900/50 divide-y divide-zinc-800/80 overflow-hidden">
          {hours.map((h) => (
            <div key={h.day} className="flex items-center justify-between px-5 py-3.5">
              <span className="text-zinc-200 font-medium">{h.day}</span>
              <span className="text-zinc-300 text-sm font-mono">{h.open}</span>
            </div>
          ))}
        </div>
        <p className="text-xs text-zinc-500 mt-3 leading-relaxed">
          {isEN
            ? "Pickup and return times are coordinated when you book — same vehicle never goes out twice at the same time."
            : "Gli orari di consegna e riconsegna sono indicativi per coordinare gli scambi: lo stesso veicolo non parte mai due volte nello stesso slot."}
        </p>
      </section>

      {/* ============ How to reach us ============ */}
      <section data-testid="home-find-us">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-full bg-[#FFD700]/15 border border-[#FFD700]/40 flex items-center justify-center">
            <MapPin className="w-4 h-4 text-[#FFD700]" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">{isEN ? "Find us" : "Vieni a trovarci"}</p>
            <h2 className="text-2xl font-semibold text-zinc-100 leading-tight">
              {isEN ? "How to reach us" : "Come raggiungerci"}
            </h2>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          <div className="space-y-5">
            <div className="rounded-2xl bg-zinc-900/60 border border-white/5 p-5" data-testid="home-address-card">
              <p className="text-xs uppercase tracking-widest text-zinc-500 mb-2">{isEN ? "Address" : "Indirizzo"}</p>
              <p className="text-lg font-semibold text-zinc-100">{BRAND_NAME}</p>
              <p className="text-zinc-300 mt-0.5">{BRAND_ADDRESS_STREET}</p>
              <p className="text-zinc-300">{BRAND_ADDRESS_CITY}</p>
              <div className="mt-4 grid grid-cols-3 gap-2">
                <a href={BRAND_MAPS_URL} target="_blank" rel="noreferrer"
                   className="flex flex-col items-center justify-center gap-1 py-2.5 rounded-xl bg-[#FFD700] text-zinc-950 font-semibold text-xs hover:brightness-110 transition"
                   data-testid="home-open-gmaps">
                  <Navigation className="w-4 h-4" /> Google Maps
                </a>
                <a href={BRAND_WAZE_URL} target="_blank" rel="noreferrer"
                   className="flex flex-col items-center justify-center gap-1 py-2.5 rounded-xl bg-[#33CCFF]/15 border border-[#33CCFF]/40 text-[#33CCFF] font-semibold text-xs hover:bg-[#33CCFF]/25 transition"
                   data-testid="home-open-waze">
                  <Navigation className="w-4 h-4" /> Waze
                </a>
                <a href={BRAND_APPLE_MAPS_URL} target="_blank" rel="noreferrer"
                   className="flex flex-col items-center justify-center gap-1 py-2.5 rounded-xl bg-zinc-800/80 border border-zinc-700 text-zinc-100 font-semibold text-xs hover:bg-zinc-800 transition"
                   data-testid="home-open-apple-maps">
                  <Navigation className="w-4 h-4" /> Apple Maps
                </a>
              </div>
            </div>

            <div className="rounded-2xl bg-zinc-900/60 border border-white/5 p-5" data-testid="home-walking-card">
              <div className="flex items-center gap-2 mb-2">
                <Footprints className="w-4 h-4 text-[#FFD700]" />
                <p className="text-xs uppercase tracking-widest text-zinc-500">{isEN ? "Walking directions" : "A piedi"}</p>
              </div>
              <p className="text-sm text-zinc-300 leading-relaxed">
                {isEN
                  ? "From the Conad supermarket, 500 metres on foot towards Poggio Marciana, following the SP25 in the opposite direction."
                  : "Dal supermercato Conad 500 metri a piedi direzione Poggio Marciana seguendo in controsenso la SP25."}
              </p>
            </div>

            <a href={BRAND_REVIEWS_URL} target="_blank" rel="noreferrer"
               className="block rounded-2xl bg-gradient-to-br from-[#FFD700]/10 to-transparent border border-[#FFD700]/30 p-5 hover:border-[#FFD700]/60 transition group"
               data-testid="home-google-reviews">
              <div className="flex items-center gap-4">
                <div className="flex flex-col items-center justify-center min-w-[58px]">
                  <span className="text-3xl font-bold text-[#FFD700] leading-none" data-testid="home-reviews-rating">
                    {BRAND_REVIEWS_RATING.toFixed(1)}
                  </span>
                  <div className="flex mt-1">
                    {[0,1,2,3,4].map((i) => {
                      const fillPct = Math.max(0, Math.min(1, BRAND_REVIEWS_RATING - i));
                      return (
                        <span key={i} className="relative inline-block w-3 h-3">
                          <Star className="absolute inset-0 w-3 h-3 text-zinc-700" />
                          {fillPct > 0 && (
                            <span className="absolute inset-0 overflow-hidden" style={{ width: `${fillPct * 100}%` }}>
                              <Star className="w-3 h-3 fill-[#FFD700] text-[#FFD700]" />
                            </span>
                          )}
                        </span>
                      );
                    })}
                  </div>
                </div>
                <div className="flex-1">
                  <p className="text-zinc-100 font-semibold leading-tight">
                    {isEN ? "Excellent on Google" : "Eccellente su Google"}
                  </p>
                  <p className="text-zinc-400 text-xs mt-0.5" data-testid="home-reviews-count">
                    {isEN ? `Based on ${BRAND_REVIEWS_COUNT} reviews` : `Basato su ${BRAND_REVIEWS_COUNT} recensioni`}
                  </p>
                  <p className="text-xs text-[#FFD700] mt-2 group-hover:translate-x-0.5 transition-transform">
                    {isEN ? "Read all reviews →" : "Leggi tutte le recensioni →"}
                  </p>
                </div>
              </div>
            </a>
          </div>

          {/* Map */}
          <div className="rounded-2xl overflow-hidden border border-white/5 bg-zinc-900/60 min-h-[420px]" data-testid="home-map">
            <iframe
              title="Rent Lupi - Google Maps"
              src={BRAND_MAP_EMBED_URL}
              width="100%"
              height="100%"
              style={{ minHeight: 420, border: 0, filter: "invert(0.92) hue-rotate(180deg) saturate(0.7)" }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </div>
      </section>
    </div>
  );
}
